import React from "react";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import requests from "../Auth/Requests";
import globals from "../Globals";
import {
    Box,
    Button,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Select,
    MenuItem,
    InputLabel,
    FormControl
} from "@mui/material";

export default class DeployPage extends React.Component {
    state = {
        launchers: [],
        plans: [],
        loading: false,
        deployDialogOpen: false,
        editServersDialogOpen: false,
        updateDialogOpen: false,
        undeployDialogOpen: false,

        // Deploy fields
        deployRegion: "",
        deployPlan: "",
        deployBranches: [],
        deployPriority: 0,

        // Edit servers dialog
        editServerLauncherId: "",
        editServerAction: "add",
        editServerBranch: globals.branches[0],
        editServerVersion: "",
        editServerCount: 1,

        // For update and undeploy
        selectedLauncher: null,
        updateBranches: [],
        updatePriority: 0,

        // Launcher dialog state
        launcherDialogOpen: false,
        launcherDialogLauncher: null,

        // Upload Launcher dialog state
        uploadLauncherDialogOpen: false,
        uploadLauncherFile: null,
    };
    // Upload Launcher dialog handlers
    openUploadLauncherDialog = () => this.setState({ uploadLauncherDialogOpen: true, uploadLauncherFile: null });
    closeUploadLauncherDialog = () => this.setState({ uploadLauncherDialogOpen: false, uploadLauncherFile: null });

    handleUploadLauncherFile = e => {
        const file = e.target.files[0];
        if (file) this.setState({ uploadLauncherFile: file });
    };

    handleUploadLauncher = async () => {
        const { uploadLauncherFile } = this.state;
        if (!uploadLauncherFile) return;
        this.setState({ loading: true });
        try {
            // Pass the file object directly (your Requests.js will send as binary)
            await this.performAction("Upload Launcher", uploadLauncherFile, "upload-launcher");
            enqueueSnackbar("Launcher uploaded!", { variant: "success" });
            this.setState({ uploadLauncherDialogOpen: false, uploadLauncherFile: null });
            this.loadData();
        } catch (error) {
            enqueueSnackbar("Upload failed: " + error.message, { variant: "error" });
            this.setState({ uploadLauncherDialogOpen: false, uploadLauncherFile: null });
        } finally {
            this.setState({ loading: false });
        }
    };

    componentDidMount() {
        this.loadData();
    }

    performAction = (description, ...args) => {
        let payload = {};
        if ((args[0] instanceof File) || (typeof args[0] === "object" && args[0] !== null)) {
            payload = args[0];
            args = args.slice(1);
        }

        const key = enqueueSnackbar(description + "...", { variant: "info", persist: true });
        return requests.request("servers deployment " + args.join(" "), payload)
            .then(response => {
                closeSnackbar(key);
                enqueueSnackbar(description + " successful!", { variant: "success" });
                if (response.launchers) {
                    if (response.plans != null)
                        this.setState({ launchers: response.launchers, plans: response.plans });
                    else
                        this.setState({ launchers: response.launchers });
                }
                return response;
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(description + " failed: " + error.message, { variant: "error" });
            });
    };

    loadData = () => {
        this.performAction("Loading launchers", "list");
    };

    // Deploy dialog
    openDeployDialog = () => {
        this.setState({
            deployDialogOpen: true,
            deployRegion: "",
            deployPlan: "",
            deployBranches: [],
            deployPriority: 0,
        });
    };
    closeDeployDialog = () => this.setState({ deployDialogOpen: false });

    handleDeploy = () => {
        const { deployRegion, deployPlan, deployBranches, deployPriority } = this.state;
        // Calculate branches flags
        let branches = 0;
        deployBranches.forEach(b =>
            branches |= globals.branchFlags[b]
        );
        this.performAction(
            "Deploying machine",
            "deploy",
            deployRegion,
            deployPlan,
            branches,
            deployPriority
        ).then(() => this.setState({ deployDialogOpen: false }));
    };

    handleDeployBranchesChange = (branch) => {
        this.setState(prev => {
            const arr = prev.deployBranches.includes(branch)
                ? prev.deployBranches.filter(b => b !== branch)
                : [...prev.deployBranches, branch];
            return { deployBranches: arr };
        });
    };

    // Undeploy
    handleUndeploy = (launcher) => {
        this.performAction("Undeploying", "undeploy", launcher.id);
    };

    // Launcher dialog handlers
    openLauncherDialog = (launcher) => {
        this.setState({
            launcherDialogOpen: true,
            launcherDialogLauncher: launcher,
        });
    };
    closeLauncherDialog = () => {
        this.setState({
            launcherDialogOpen: false,
            launcherDialogLauncher: null,
        });
    };

    handleLauncherUpdate = () => {
        const { launcherDialogLauncher } = this.state;
        if (!launcherDialogLauncher) return;
        this.performAction(
            "Updating launcher",
            "update-launcher",
            launcherDialogLauncher.id
        ).then(() => this.closeLauncherDialog());
    };
    handleLauncherRestart = () => {
        const { launcherDialogLauncher } = this.state;
        if (!launcherDialogLauncher) return;
        this.performAction(
            "Restarting launcher",
            "restart-launcher",
            launcherDialogLauncher.id
        ).then(() => this.closeLauncherDialog());
    };
    handleLauncherUndeploy = () => {
        const { launcherDialogLauncher } = this.state;
        if (!launcherDialogLauncher) return;
        this.performAction(
            "Undeploying",
            "undeploy",
            launcherDialogLauncher.id
        ).then(() => this.closeLauncherDialog());
    };

    // Update dialog (priority, branches)
    openUpdateDialog = (launcher) => {
        // Get current branches as array of names
        const checkedBranches = globals.branches.filter(b => (launcher.branches & globals.branchFlags[b]));
        this.setState({
            updateDialogOpen: true,
            selectedLauncher: launcher,
            updateBranches: checkedBranches,
            updatePriority: launcher.priority || 0,
        });
    };
    closeUpdateDialog = () => this.setState({ updateDialogOpen: false });

    handleUpdate = () => {
        const { selectedLauncher, updateBranches, updatePriority } = this.state;
        let branches = 0;
        updateBranches.forEach(b =>
            branches |= globals.branchFlags[b]
        );
        this.performAction(
            "Updating launcher",
            "update",
            selectedLauncher.id,
            branches,
            updatePriority
        ).then(() => this.setState({ updateDialogOpen: false }));
    };

    handleUpdateBranchesChange = (branch) => {
        this.setState(prev => {
            const arr = prev.updateBranches.includes(branch)
                ? prev.updateBranches.filter(b => b !== branch)
                : [...prev.updateBranches, branch];
            return { updateBranches: arr };
        });
    };

    // Edit servers dialog (add/remove)
    openEditServersDialog = (launcher) => {
        this.setState({
            editServersDialogOpen: true,
            editServerLauncherId: launcher.id,
            editServerAction: "add",
            editServerBranch: globals.branches[0],
            editServerVersion: "",
            editServerCount: 1,
        });
    };
    closeEditServersDialog = () => this.setState({ editServersDialogOpen: false });

    handleEditServers = () => {
        const {
            editServerLauncherId, editServerAction,
            editServerBranch, editServerVersion, editServerCount
        } = this.state;
        // Parse version and branch to compact version
        const [major, minor, patch] = (editServerVersion || "0.0.0").split('.').map(x => parseInt(x) || 0);
        const branch = globals.branches.indexOf(editServerBranch);
        const compact = globals.compactVersion(major, minor, patch, branch);
        this.performAction(
            (editServerAction === "add" ? "Adding" : "Removing") + " servers",
            (editServerAction === "add" ? "add-server" : "remove-server"),
            editServerLauncherId,
            compact,
            editServerCount
        ).then(() => this.setState({ editServersDialogOpen: false }));
    };

    render() {
        const {
            launchers, plans, deployDialogOpen, deployRegion, deployPlan, deployBranches, deployPriority,
            editServersDialogOpen, editServerAction, editServerBranch, editServerVersion, editServerCount,
            updateDialogOpen, updateBranches, updatePriority
        } = this.state;

        return (
            <Box sx={{ maxWidth: 1000, margin: "40px auto", padding: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <h2>Deployed Launchers</h2>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Button variant="contained" sx={{ mr: 2 }} onClick={this.openUploadLauncherDialog}>Upload Launcher</Button>
                        <Button variant="contained" onClick={this.openDeployDialog}>Deploy Machine</Button>
                    </Box>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Region</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Branches</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {launchers.map(launcher => (
                                <TableRow key={launcher.machineId}>
                                    <TableCell>{launcher.region}</TableCell>
                                    <TableCell>{launcher.machinePlan}</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const filtered = globals.branches.filter(b => (launcher.branches & globals.branchFlags[b]));
                                            if (filtered.length === 0)
                                                return "Manual";
                                            return filtered.length === 1
                                                ? filtered[0]
                                                : filtered.map(b => b[0]).join(", ");
                                        })()}
                                    </TableCell>
                                    <TableCell>{launcher.priority}</TableCell>
                                    <TableCell>
                                        {launcher.stopping ? (
                                            <span style={{ color: "#c00" }}>STOPPING</span>
                                        ) : (
                                            <>
                                                <Button color="primary" size="small" onClick={() => this.openEditServersDialog(launcher)}>View</Button>
                                                <Button color="primary" size="small" onClick={() => this.openUpdateDialog(launcher)} sx={{ ml: 1 }}>Edit</Button>
                                                <Button color="secondary" size="small" onClick={() => this.openEditServersDialog(launcher)} sx={{ ml: 1 }}>Servers</Button>
                                                <Button color="info" size="small" onClick={() => this.openLauncherDialog(launcher)} sx={{ ml: 1 }}>Launcher</Button>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Deploy Dialog */}
                <Dialog open={deployDialogOpen} onClose={this.closeDeployDialog}>
                    <DialogTitle>Deploy Machine</DialogTitle>
                    <DialogContent>
                        <TextField label="Region" fullWidth value={deployRegion} onChange={e => this.setState({ deployRegion: e.target.value })} sx={{ my: 1 }} />
                        <FormControl fullWidth sx={{ my: 1 }}>
                            <InputLabel>Plan</InputLabel>
                            <Select
                                value={deployPlan}
                                onChange={e => this.setState({ deployPlan: e.target.value })}
                                label="Plan"
                            >
                                {plans.map(plan => <MenuItem key={plan} value={plan}>{plan}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormGroup row sx={{ my: 1 }}>
                            {globals.branches.map(b =>
                                <FormControlLabel
                                    key={b}
                                    control={
                                        <Checkbox
                                            checked={deployBranches.includes(b)}
                                            onChange={() => this.handleDeployBranchesChange(b)}
                                            value={b}
                                        />
                                    }
                                    label={b}
                                />
                            )}
                        </FormGroup>
                        <TextField label="Priority" type="number" fullWidth value={deployPriority} onChange={e => this.setState({ deployPriority: parseInt(e.target.value) })} sx={{ my: 1 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeDeployDialog}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleDeploy}>Deploy</Button>
                    </DialogActions>
                </Dialog>

                {/* Edit Servers Dialog */}
                <Dialog open={editServersDialogOpen} onClose={this.closeEditServersDialog}>
                    <DialogTitle>Edit Servers</DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ my: 1 }}>
                            <InputLabel>Action</InputLabel>
                            <Select
                                value={editServerAction}
                                onChange={e => this.setState({ editServerAction: e.target.value })}
                                label="Action"
                            >
                                <MenuItem value="add">Add</MenuItem>
                                <MenuItem value="remove">Remove</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ my: 1 }}>
                            <InputLabel>Branch</InputLabel>
                            <Select
                                value={editServerBranch}
                                onChange={e => this.setState({ editServerBranch: e.target.value })}
                                label="Branch"
                            >
                                {globals.branches.map(b =>
                                    <MenuItem key={b} value={b}>{b}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <TextField label="Version" fullWidth value={editServerVersion} onChange={e => this.setState({ editServerVersion: e.target.value })} sx={{ my: 1 }} />
                        <TextField label="Count" type="number" fullWidth value={editServerCount} onChange={e => this.setState({ editServerCount: parseInt(e.target.value) })} sx={{ my: 1 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeEditServersDialog}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleEditServers}>Submit</Button>
                    </DialogActions>
                </Dialog>

                {/* Update Dialog */}
                <Dialog open={updateDialogOpen} onClose={this.closeUpdateDialog}>
                    <DialogTitle>Update Launcher</DialogTitle>
                    <DialogContent>
                        <FormGroup row sx={{ my: 1 }}>
                            {globals.branches.map(b =>
                                <FormControlLabel
                                    key={b}
                                    control={
                                        <Checkbox
                                            checked={updateBranches && updateBranches.includes(b)}
                                            onChange={() => this.handleUpdateBranchesChange(b)}
                                            value={b}
                                        />
                                    }
                                    label={b}
                                />
                            )}
                        </FormGroup>
                        <TextField label="Priority" type="number" fullWidth value={updatePriority} onChange={e => this.setState({ updatePriority: parseInt(e.target.value) })} sx={{ my: 1 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeUpdateDialog}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleUpdate}>Update</Button>
                    </DialogActions>
                </Dialog>
                {/* Launcher Dialog */}
                <Dialog open={this.state.launcherDialogOpen} onClose={this.closeLauncherDialog}>
                    <DialogTitle>Launcher Actions</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, mb: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={this.handleLauncherUpdate}
                                sx={{ mb: 1 }}
                            >
                                Update
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={this.handleLauncherRestart}
                                sx={{ mb: 1 }}
                            >
                                Restart
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={this.handleLauncherUndeploy}
                            >
                                Undeploy
                            </Button>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeLauncherDialog}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                {/* Upload Launcher Dialog */}
                <Dialog open={this.state.uploadLauncherDialogOpen} onClose={this.closeUploadLauncherDialog}>
                    <DialogTitle>Upload Launcher (.zip)</DialogTitle>
                    <DialogContent>
                        <input
                            type="file"
                            accept=".zip"
                            onChange={this.handleUploadLauncherFile}
                            style={{ marginTop: 16 }}
                        />
                        {this.state.uploadLauncherFile && (
                            <div style={{ marginTop: 12 }}>
                                Selected: {this.state.uploadLauncherFile.name}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeUploadLauncherDialog}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={this.handleUploadLauncher}
                            disabled={!this.state.uploadLauncherFile || this.state.loading}
                        >Upload</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }
}